import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import type { TableOptions } from 'react-table';
import { useTable } from 'react-table';
import { faArrowDown } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { RequireAtLeastOne } from '@soundxyz/utils/helpers';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import type { Timespan } from '@components/Charts/TimespanSelector';
import { TimespanSelector } from '@components/Charts/TimespanSelector';
import { ObjectDropdownSelect } from '@components/main-components/selects/ObjectDropdownSelect';
import { InfoTooltip } from '@components/main-components/tooltip/InfoTooltip';
import { MediaQuery } from '@context/mediaQuery';
import type {
  TrendingArtistInfoQuery,
  TrendingCollectorsQuery,
  TrendingCuratorsQuery,
  TrendingReleasesQuery,
} from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import type {
  TopArtistsSortSubpaths,
  TopCollectorsSortSubpaths,
  TopCuratorsSortSubpaths,
  TopReleasesSortSubpaths,
} from '@pages-shared/topCharts';
import {
  isTopArtistsSortSubpaths,
  isTopCollectorsSortSubpaths,
  isTopCuratorsSortSubpaths,
  isTopReleasesSortSubpaths,
} from '@pages-shared/topCharts';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import type {
  TrendingArtistAccessorType,
  TrendingCollectorAccessorType,
  TrendingReleaseAccessorType,
} from '../consts';
import {
  ARTIST_COLUMNS,
  COLLECTOR_COLUMNS,
  CURATOR_COLUMNS,
  RELEASE_COLUMNS,
  TRENDING_ARTIST_ACCESSOR_TO_SORT,
  TRENDING_COLLECTOR_ACCESSOR_TO_SORT,
  TRENDING_CURATOR_ACCESSOR_TO_SORT,
  TRENDING_RELEASE_ACCESSOR_TO_SORT,
  TRENDING_SORT_TO_DISPLAY,
} from '../consts';
import { TrendingTableRow } from './TableRow';
import type { ArtistRow, CollectorRow, CuratorRow, ReleaseRow } from './types';

const createReleaseUseTableData = (
  releaseData: TrendingReleasesQuery['topReleases'] = [],
  numDecimals: number,
) => {
  return {
    data:
      releaseData.reduce<ReleaseRow[]>((acc, releaseBlob) => {
        if (!releaseBlob.release) return acc;

        const eth = formatWeiToEthForDisplay({
          weiValue: releaseBlob.totalSales,
          maxDecimals: numDecimals,
        });

        if (!eth) return acc;

        acc.push({
          uniqueCollectors: releaseBlob.uniqueCollectors.toLocaleString(),
          nftsSold: releaseBlob.nftsSold.toLocaleString(),
          totalSales: {
            eth,
          },
          rank: acc.length + 1,
          releaseInfo: {
            title: releaseBlob.release.title,
            coverUrl:
              releaseBlob.release.staticCoverImage?.url || releaseBlob.release.coverImage.url,
            webappUri: releaseBlob.release.webappUri,
            artistInfo: {
              name: releaseBlob.release.artist.name,
              webappUri: releaseBlob.release.artist.webappUri,
            },
          },
        });

        return acc;
      }, []) || [],
    columns: RELEASE_COLUMNS(),
  };
};

const createCollectorUseTableData = (
  fullText: boolean,
  collectorData: TrendingCollectorsQuery['topCollectorsV2'] = [],
  numDecimals: number,
) => {
  return {
    data:
      collectorData.reduce<CollectorRow[]>((acc, collectorBlob) => {
        if (!collectorBlob.user) return acc;

        const eth = formatWeiToEthForDisplay({
          weiValue: collectorBlob.totalSpent,
          maxDecimals: numDecimals,
        });

        if (!eth) return acc;

        acc.push({
          creatorsSupported: collectorBlob.creatorsSupported.toLocaleString(),
          totalSpent: {
            eth,
          },
          nftsBought: collectorBlob.nftsBought.toLocaleString(),
          userInfo: {
            address: collectorBlob.user.publicAddress,
            handle: collectorBlob.user.twitterHandle,
            avatarUrl: collectorBlob.user.avatar?.url,
            username: collectorBlob.user.username,
            artist: collectorBlob.user.artist
              ? {
                  webappUri: collectorBlob.user.artist.webappUri,
                  name: collectorBlob.user.artist.name,
                  soundHandle: collectorBlob.user.artist.soundHandle,
                }
              : null,
            webappUri: collectorBlob.user.webappUri,
          },
          rank: acc.length + 1,
        });

        return acc;
      }, []) || [],
    columns: COLLECTOR_COLUMNS(fullText),
  };
};

const createArtistUseTableData = (
  artistData: TrendingArtistInfoQuery['topArtistsV2'] = [],
  numDecimals: number,
) => {
  return {
    data: artistData.reduce<ArtistRow[]>(
      (acc, { uniqueCollectors, nftsSold, artist, totalSales }) => {
        if (!artist?.user) return acc;

        const eth = formatWeiToEthForDisplay({ weiValue: totalSales, maxDecimals: numDecimals });

        if (!eth) return acc;

        acc.push({
          uniqueCollectors: uniqueCollectors.toLocaleString(),
          nftsSold: nftsSold.toLocaleString(),
          totalSales: {
            eth,
          },
          rank: acc.length + 1,
          address: artist.user.publicAddress,
          artistInfo: {
            name: artist.name,
            address: artist.user.publicAddress,
            twitterHandle: artist.user.twitterHandle,
            avatarUrl: artist.user.avatar?.url,
            webappUri: artist.webappUri,
            username: artist.user.username,
            soundHandle: artist.soundHandle,
          },
        });

        return acc;
      },
      [],
    ),
    columns: ARTIST_COLUMNS(),
  };
};

function createCuratorUseTableData({
  curatorData = [],
  numDecimals,
}: {
  curatorData: TrendingCuratorsQuery['topAffiliateCurators'] | undefined;
  numDecimals: number;
}) {
  return {
    data: curatorData.reduce<CuratorRow[]>((acc, value) => {
      if (!value.user) return acc;

      const totalVolume = formatWeiToEthForDisplay({
        weiValue: value.totalVolume,
        maxDecimals: numDecimals,
      });

      if (!totalVolume) return acc;

      acc.push({
        rank: acc.length + 1,
        mintsQuantity: value.mintsQuantity,
        soundsReferred: value.soundsReferred,
        userInfo: {
          address: value.user.publicAddress,
          handle: value.user.twitterHandle,
          avatarUrl: value.user.avatar?.url,
          username: value.user.username,
          artist: value.user.artist
            ? {
                webappUri: value.user.artist.webappUri,
                name: value.user.artist.name,
                soundHandle: value.user.artist.soundHandle,
              }
            : null,
          webappUri: value.user.webappUri,
        },
        totalVolume,
      });

      return acc;
    }, []),
    columns: CURATOR_COLUMNS(),
  };
}

const rootStyles = ({ artistOrReleaseEnabled }: { artistOrReleaseEnabled: boolean }) => {
  return {
    border: 'none',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1) !important',
    borderRadius: 8,
    marginLeft: 'auto',
    backgroundColor: 'white',
    height: 33,
    width: 160,
    textAlign: 'center',
    justifyContent: 'center',

    '@tabletLandscape': {
      width: artistOrReleaseEnabled ? 160 : 180,
    },
  };
};

const dropdownStyles = { textAlign: 'left', marginLeft: 'auto', marginTop: 10 };

const artistOrReleaseDropdownStyles = {
  ...dropdownStyles,
  minWidth: 160,
};

const collectorDropdownStyles = {
  ...dropdownStyles,
  minWidth: 160,
  '@tabletLandscape': {
    minWidth: 180,
  },
};

const triggerStyles = {
  fontWeight: 600,
  justifyContent: 'space-between',
  columnGap: 0,
};

const artistOrReleaseTriggerStyles = {
  ...triggerStyles,
  width: 137,
};

const collectorTriggerStyles = {
  ...triggerStyles,
  width: 137,
  '@tabletLandscape': {
    width: 165,
  },
};

type OnTopReleaseSortClick = (sort: TopReleasesSortSubpaths) => void;
type OnTopArtistSortClick = (sort: TopArtistsSortSubpaths) => void;
type OnTopCollectorSortClick = (sort: TopCollectorsSortSubpaths) => void;
type OnTopCuratorSortClick = (sort: TopCuratorsSortSubpaths) => void;

export function TrendingTable(
  props: {
    loading?: boolean;
    activeTimespan: Timespan;
    setActiveTimespan: (timeSpan: Timespan) => void;
    sort:
      | TopReleasesSortSubpaths
      | TopArtistsSortSubpaths
      | TopCollectorsSortSubpaths
      | TopCuratorsSortSubpaths;
  } & RequireAtLeastOne<{
    releaseTrendingData?: TrendingReleasesQuery['topReleases'];
    artistTrendingData?: TrendingArtistInfoQuery['topArtistsV2'];
    collectorTrendingData?: TrendingCollectorsQuery['topCollectorsV2'];
    curatorTrendingData?: TrendingCuratorsQuery['topAffiliateCurators'];
  }> &
    RequireAtLeastOne<{
      onTopReleaseSortClick?: OnTopReleaseSortClick;
      onTopArtistSortClick?: OnTopArtistSortClick;
      onTopCollectorSortClick?: OnTopCollectorSortClick;
      onTopCuratorSortClick?: OnTopCuratorSortClick;
    }>,
) {
  const {
    releaseTrendingData,
    artistTrendingData,
    collectorTrendingData,
    curatorTrendingData,
    activeTimespan,
    setActiveTimespan,
    sort: sortedColumn,
    onTopReleaseSortClick,
    onTopArtistSortClick,
    onTopCollectorSortClick,
    onTopCuratorSortClick,
  } = props;

  const { tablet, tabletLandscape, laptop } = MediaQuery.useContainer();

  const numDecimals = tablet ? 3 : 2;

  const releaseTabEnabled = !!releaseTrendingData;

  const artistTabEnabled = !!artistTrendingData;

  const artistOrReleaseTabEnabled = artistTabEnabled || releaseTabEnabled;

  const collectorsTabEnabled = !!collectorTrendingData;

  const curatorsTabEnabled = !!curatorTrendingData;

  const releaseTableData = React.useMemo(
    () =>
      releaseTabEnabled
        ? createReleaseUseTableData(releaseTrendingData, numDecimals)
        : {
            data: [],
            columns: [],
          },
    [releaseTrendingData, releaseTabEnabled, numDecimals],
  );

  const artistTableData = React.useMemo(
    () =>
      artistTabEnabled
        ? createArtistUseTableData(artistTrendingData, numDecimals)
        : {
            data: [],
            columns: [],
          },
    [artistTrendingData, artistTabEnabled, numDecimals],
  );

  const collectorTableData = React.useMemo(
    () =>
      collectorsTabEnabled
        ? createCollectorUseTableData(tabletLandscape, collectorTrendingData, numDecimals)
        : {
            data: [],
            columns: [],
          },
    [tabletLandscape, collectorTrendingData, collectorsTabEnabled, numDecimals],
  );

  const curatorTableData = React.useMemo(
    () =>
      curatorsTabEnabled
        ? createCuratorUseTableData({
            curatorData: curatorTrendingData,
            numDecimals,
          })
        : {
            data: [],
            columns: [],
          },
    [curatorTrendingData, curatorsTabEnabled, numDecimals],
  );

  const dataToPass =
    (releaseTabEnabled
      ? releaseTableData
      : artistTabEnabled
      ? artistTableData
      : collectorsTabEnabled
      ? collectorTableData
      : curatorTableData) || [];

  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } = useTable<
    ReleaseRow | ArtistRow | CollectorRow | CuratorRow
  >({
    columns: dataToPass.columns as unknown as TableOptions<
      ReleaseRow | ArtistRow | CollectorRow | CuratorRow
    >['columns'],
    data: dataToPass.data as unknown as TableOptions<
      ReleaseRow | ArtistRow | CollectorRow | CuratorRow
    >['data'],
  });

  const { columns } = dataToPass;

  const sortedColumnDisplayName = TRENDING_SORT_TO_DISPLAY(tabletLandscape)[sortedColumn];

  const onSelectColumn = useStableCallback((selection: { [key: string]: string }) => {
    if (!selection.Header || !selection.accessor) {
      return;
    }

    if (releaseTabEnabled && selection.Header && selection.accessor) {
      const releaseSortEnumValue =
        TRENDING_RELEASE_ACCESSOR_TO_SORT[selection.accessor as TrendingReleaseAccessorType];

      if (isTopArtistsSortSubpaths(releaseSortEnumValue)) {
        onTopReleaseSortClick?.(releaseSortEnumValue);
      }
    } else if (artistTabEnabled && selection.Header && selection.accessor) {
      const artistSortEnumValue =
        TRENDING_ARTIST_ACCESSOR_TO_SORT[selection.accessor as TrendingArtistAccessorType];
      if (isTopArtistsSortSubpaths(artistSortEnumValue)) {
        onTopArtistSortClick?.(artistSortEnumValue);
      }
    } else if (collectorsTabEnabled) {
      const collectorSortEnumValue =
        TRENDING_COLLECTOR_ACCESSOR_TO_SORT[selection.accessor as TrendingCollectorAccessorType];
      if (isTopCollectorsSortSubpaths(collectorSortEnumValue)) {
        onTopCollectorSortClick?.(collectorSortEnumValue);
      }
    } else if (curatorsTabEnabled) {
      const curatorSortEnumValue =
        TRENDING_CURATOR_ACCESSOR_TO_SORT[selection.accessor as TrendingCollectorAccessorType];
      if (isTopCuratorsSortSubpaths(curatorSortEnumValue)) {
        onTopCuratorSortClick?.(curatorSortEnumValue);
      }
    }
  });

  return (
    <TableWrapper>
      <FilterOptions>
        {!laptop && (
          <ObjectDropdownSelect
            options={columns.slice(2)}
            displayKey="Header"
            valueKey="accessor"
            selection={sortedColumnDisplayName}
            setSelection={(selection: { [key: string]: string }) => onSelectColumn(selection)}
            headerType="text"
            variant="end"
            dropdownStyles={
              artistOrReleaseTabEnabled ? artistOrReleaseDropdownStyles : collectorDropdownStyles
            }
            rootStyles={rootStyles({
              artistOrReleaseEnabled: artistOrReleaseTabEnabled,
            })}
            triggerStyles={
              artistOrReleaseTabEnabled ? artistOrReleaseTriggerStyles : collectorTriggerStyles
            }
          />
        )}
        <TimespanSelector
          activeTimespan={activeTimespan}
          setActiveTimespan={setActiveTimespan}
          variant="table"
        />
        <TimespanSelector
          activeTimespan={activeTimespan}
          setActiveTimespan={setActiveTimespan}
          variant="heading"
        />
      </FilterOptions>

      <Table {...getTableProps()}>
        <TheadStyled>
          <tr>
            {headers.map((column, i) => {
              const columnId = artistOrReleaseTabEnabled
                ? TRENDING_ARTIST_ACCESSOR_TO_SORT[
                    column.id as keyof typeof TRENDING_ARTIST_ACCESSOR_TO_SORT
                  ]
                : collectorsTabEnabled
                ? TRENDING_COLLECTOR_ACCESSOR_TO_SORT[
                    column.id as keyof typeof TRENDING_COLLECTOR_ACCESSOR_TO_SORT
                  ]
                : TRENDING_CURATOR_ACCESSOR_TO_SORT[
                    column.id as keyof typeof TRENDING_CURATOR_ACCESSOR_TO_SORT
                  ];

              const isSortableColumn = releaseTabEnabled
                ? isTopReleasesSortSubpaths(columnId)
                : artistTabEnabled
                ? isTopArtistsSortSubpaths(columnId)
                : collectorsTabEnabled
                ? isTopCollectorsSortSubpaths(columnId)
                : isTopCuratorsSortSubpaths(columnId);

              const ThToUse = artistOrReleaseTabEnabled
                ? columnId === sortedColumn
                  ? ThB
                  : isSortableColumn
                  ? SortableTh
                  : Th
                : columnId === sortedColumn
                ? ThCollectorsB
                : isSortableColumn
                ? SortableThCollectors
                : ThCollectors;

              const headerProps = column.getHeaderProps();

              return (
                <ThToUse
                  {...headerProps}
                  key={i}
                  className="group"
                  type={column.id !== 'rank' ? 'numerical' : 'textual'}
                  onClick={event => {
                    if (columnId === sortedColumn) {
                      event.stopPropagation();
                      event.preventDefault();

                      return;
                    } else {
                      if (releaseTabEnabled && isTopReleasesSortSubpaths(columnId)) {
                        onTopReleaseSortClick?.(columnId);
                      } else if (artistTabEnabled && isTopArtistsSortSubpaths(columnId)) {
                        onTopArtistSortClick?.(columnId);
                      } else if (collectorsTabEnabled && isTopCollectorsSortSubpaths(columnId)) {
                        onTopCollectorSortClick?.(columnId);
                      } else if (isTopCuratorsSortSubpaths(columnId)) {
                        onTopCuratorSortClick?.(columnId);
                      }
                    }
                    //@ts-expect-error TODO: https://linear.app/sound/issue/ENG-2306/improve-trending-table-onclick-even-types
                    headerProps.onClick?.(event);
                  }}
                >
                  <div
                    className={clsx(
                      'ml-auto flex items-center gap-1',
                      column.id !== 'rank' && 'w-fit',
                      columnId === sortedColumn ? '[&_svg]:text-black' : '[&_svg]:text-grey400',
                    )}
                  >
                    {column.render('Header')}
                    <div className="hidden normal-case group-last-of-type:flex">
                      <InfoTooltip openOnClick={!tabletLandscape} variant="outlined" size={12}>
                        <InfoTooltip.Content size="xs">
                          Only includes paid mints
                        </InfoTooltip.Content>
                      </InfoTooltip>
                    </div>
                  </div>
                  <StyledFontAwesomeIcon icon={faArrowDown} />
                </ThToUse>
              );
            })}
          </tr>
        </TheadStyled>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIdx) => {
            prepareRow(row);

            const key =
              'userInfo' in row.original
                ? row.original.userInfo.address
                : 'releaseInfo' in row.original
                ? row.original.releaseInfo.title
                : row.original.address;
            return (
              <TrendingTableRow
                key={key}
                row={row}
                rowIdx={rowIdx}
                isTablet={tablet}
                isLaptop={laptop}
                selectedColumn={sortedColumnDisplayName}
                loading={props.loading}
              />
            );
          })}
        </tbody>
      </Table>
    </TableWrapper>
  );
}

const TableWrapper = styled('div', {
  width: '100%',

  '@tablet': {
    paddingRight: 8,
  },
});

const FilterOptions = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const TheadStyled = styled('thead', {
  display: 'none',
  borderRadius: '6px',
  marginBottom: '.3rem',
  position: 'relative',
  '@laptop': {
    display: 'table-header-group',
  },
});

const commonTh = {
  position: 'relative',
  fontFamily: '$base',
  color: '$grey400',
  whiteSpace: 'nowrap',
  fontWeight: '700',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  lineHeight: '1.5rem',
  width: 150,

  '&:first-of-type': {
    width: '50px',
    '@tablet': {
      width: '5%',
    },
  },
  '&:nth-of-type(2)': {
    width: '40%',
  },
  '&:last-of-type': {
    textAlign: 'right',
  },
  variants: {
    columnSize: {
      large: {
        flexGrow: 12,
      },
    },
    color: {
      grey: {
        color: '$grey400',
      },
    },
    type: {
      numerical: {
        textAlign: 'right',
      },
      textual: {
        textAlign: 'left',
      },
    },
  },
} satisfies CSS;

const active = {
  '.svg-inline--fa': {
    display: 'inline-block',
  },

  '@tablet': {
    color: '$grey900',
  },
  '@desktop': {
    color: '$grey900',
  },
};

const StyledFontAwesomeIcon = styled(FontAwesomeIcon, {
  position: 'absolute',
  right: -14,
  bottom: 6,

  '&.svg-inline--fa': {
    display: 'none', // Don't show by default
  },
});

const ThCollectors = styled('th', {
  textAlign: 'center',
  ...commonTh,
});
const SortableThCollectors = styled('th', {
  textAlign: 'center',
  cursor: 'pointer',
  ...commonTh,
});
const ThCollectorsB = styled('th', {
  textAlign: 'center',
  cursor: 'pointer',
  ...commonTh,
  ...active,
});

const Th = styled('th', {
  textAlign: 'left',
  ...commonTh,
});

const SortableTh = styled('th', {
  textAlign: 'left',
  cursor: 'pointer',
  ...commonTh,

  '&:hover': {
    '.svg-inline--fa': {
      display: 'inline-block',
    },
  },
});

const ThB = styled('th', {
  textAlign: 'left',
  cursor: 'pointer',
  ...commonTh,
  ...active,
});

const Table = styled('table', {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0 1rem',
  marginTop: 0,
});
